/** One compiled wiki page: TOC, cited content, cited sources, change log (child route of the app shell) */

import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Pin, PinOff } from 'lucide-react'
import { toast } from 'sonner'
import { useSetWikiPins, useWikiPage } from '../hooks/use-project-content'
import { useAppStore } from '../stores/app-store'
import { renderCitations } from '../lib/wiki-citations'
import { WikiMarkdown } from '../components/wiki/wiki-markdown'
import { projectPath } from '../components/shell/nav-config'
import { badgeClass } from '../components/ui/styles'
import { useT } from '../i18n'

export function ProjectWikiPageView() {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const { projectSlug = 'default', pageSlug } = useParams<{ projectSlug: string; pageSlug: string }>()
  const { data: page, isLoading, error } = useWikiPage(projectSlug, pageSlug)
  const setPins = useSetWikiPins(projectSlug, pageSlug ?? '')

  const rendered = useMemo(
    () => (page ? renderCitations(page.content, (id) => projectPath(projectSlug, `sources/${id}`)) : null),
    [page, projectSlug],
  )

  if (isLoading) return <p className="px-4 py-8 text-sm text-fg-subtle md:px-8">{t('common.loading')}</p>
  if (error || !page || !rendered) return <p role="alert" className="px-4 py-8 text-sm text-danger md:px-8">{t('wiki.notFound')}</p>

  const sourceById = new Map(page.sources.map((s) => [s.id, s]))
  const pinned = new Set(page.pinnedAnchors)
  const togglePin = (anchor: string) => {
    setPins.mutate({ anchor, pinned: !pinned.has(anchor) }, {
      onError: (err) => toast.error(t('wiki.pinFailed', { message: err instanceof Error ? err.message : String(err) })),
    })
  }
  const dateTime = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' })

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6 md:px-8 md:py-10">
      <Link to={projectPath(projectSlug, 'wiki')} className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('wiki.back')}
      </Link>
      <div className="mt-4 grid gap-8 lg:grid-cols-[minmax(0,1fr)_16rem]">
        <article className="min-w-0 space-y-4">
          <header className="space-y-1">
            {page.kind === 'question' && <span className={badgeClass}>{t('wiki.savedQuestion')}</span>}
            <h1 className="break-words font-display text-3xl font-semibold tracking-tight text-fg">{page.title}</h1>
            <p className="text-sm text-fg-subtle">
              {page.topic ? `${page.topic} · ` : ''}
              {t('wiki.version', { version: page.version })} · {t('wiki.cited', { percent: Math.round(page.citationCoverage * 100) })}
            </p>
          </header>
          {/* Server-compiled pages are model output: remote images never load and remote links are not clickable */}
          <WikiMarkdown content={rendered.markdown} generated={page.compiledBy === 'server'} />
        </article>

        <aside className="space-y-6 text-sm lg:sticky lg:top-6 lg:self-start">
          {page.toc.length > 0 && (
            <nav aria-label={t('wiki.contents')} className="space-y-2">
              <h2 className="font-semibold text-fg">{t('wiki.contents')}</h2>
              <ul className="space-y-1">
                {page.toc.map((entry) => (
                  <li key={entry.anchor} className="group flex items-start gap-1" style={{ paddingLeft: `${(entry.level - 1) * 0.75}rem` }}>
                    <a href={`#${entry.anchor}`} className="min-w-0 flex-1 text-fg-muted hover:text-accent-text">{entry.text}</a>
                    <button
                      type="button"
                      disabled={setPins.isPending}
                      onClick={() => togglePin(entry.anchor)}
                      aria-pressed={pinned.has(entry.anchor)}
                      aria-label={t(pinned.has(entry.anchor) ? 'wiki.unpin' : 'wiki.pin')}
                      title={t(pinned.has(entry.anchor) ? 'wiki.pinned' : 'wiki.pin')}
                      className={`shrink-0 cursor-pointer rounded p-0.5 hover:bg-surface-hover ${pinned.has(entry.anchor) ? 'text-accent-text' : 'text-fg-subtle opacity-0 group-hover:opacity-100 focus-visible:opacity-100'}`}
                    >
                      {pinned.has(entry.anchor) ? <Pin className="h-3.5 w-3.5" aria-hidden="true" /> : <PinOff className="h-3.5 w-3.5" aria-hidden="true" />}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          <section className="space-y-2">
            <h2 className="font-semibold text-fg">{t('wiki.citedSources')}</h2>
            <ol className="space-y-1.5">
              {rendered.order.map((id, i) => {
                const source = sourceById.get(id)
                return (
                  <li key={id} className="flex gap-2">
                    <span className="tabular-nums text-fg-subtle">[{i + 1}]</span>
                    <Link to={projectPath(projectSlug, `sources/${id}`)} className="min-w-0 break-words text-fg-muted hover:text-accent-text">
                      {source ? source.title : id}
                      {source && source.status !== 'active' && <span className="text-fg-subtle"> · {t(`sources.status.${source.status}`)}</span>}
                    </Link>
                  </li>
                )
              })}
            </ol>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-fg">{t('wiki.changes')}</h2>
            <ul className="space-y-2">
              {page.changes.map((change) => (
                <li key={change.version} className="text-fg-muted">
                  <span className="block text-xs text-fg-subtle">
                    {t('wiki.version', { version: change.version })} · {dateTime.format(new Date(change.createdAt))}
                  </span>
                  {change.summary}
                </li>
              ))}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  )
}
